import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { targetApi, loyverseApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Target, Lightbulb, Calendar, TrendingUp } from 'lucide-react';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { formatNumber } from '../lib/utils';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const Targets = () => {
  const { selectedBusinessId, canViewRevenue, user } = useAuthStore();
  const [targets, setTargets] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  
  // Filter state - default to current month
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  
  // Check if user is admin (can see all targets)
  const isAdmin = user?.role?.name === 'master_admin';
  const userBranchIds = user?.branch_ids || [];
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    store_id: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    quantity_target: 0,
    revenue_target: 0,
    category_ids: [],
  });

  const isManager = canViewRevenue();

  const fetchData = async () => {
    if (!selectedBusinessId) return;
    
    setLoading(true);
    try {
      const params = { year: filterYear };
      if (filterMonth) params.month = filterMonth;
      
      const [targetsRes, storesRes, categoriesRes] = await Promise.all([
        targetApi.getAll(selectedBusinessId, params),
        loyverseApi.getStores(selectedBusinessId),
        loyverseApi.getCategories(selectedBusinessId),
      ]);
      
      let filteredTargets = targetsRes.data;
      
      // For non-admin users, filter targets to only show their assigned branches
      if (!isAdmin && userBranchIds.length > 0) {
        filteredTargets = filteredTargets.filter(target => {
          // Show targets that match user's branches or have no specific branch (all branches)
          return !target.store_id || userBranchIds.includes(target.store_id);
        });
      }
      
      setTargets(filteredTargets);
      setStores(storesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load targets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBusinessId, filterYear, filterMonth, isAdmin, userBranchIds.length]);

  const fetchSuggestion = async () => {
    if (!selectedBusinessId || !isManager) return;
    
    try {
      const params = {
        year: formData.year,
        month: formData.month,
        store_id: formData.store_id || undefined,
      };
      const response = await targetApi.suggest(selectedBusinessId, params);
      setSuggestion(response.data);
    } catch (error) {
      console.error('Failed to get suggestion:', error);
    }
  };

  useEffect(() => {
    if (dialogOpen && isManager) {
      fetchSuggestion();
    }
  }, [dialogOpen, formData.year, formData.month, formData.store_id]);

  const handleOpenDialog = (target = null) => {
    if (target) {
      setSelectedTarget(target);
      setFormData({
        name: target.name || '',
        store_id: target.store_id || '',
        year: target.year,
        month: target.month,
        quantity_target: target.quantity_target,
        revenue_target: target.revenue_target,
        category_ids: target.category_ids || [],
      });
    } else {
      setSelectedTarget(null);
      setFormData({
        name: '',
        store_id: '',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        quantity_target: 0,
        revenue_target: 0,
        category_ids: [],
      });
    }
    setSuggestion(null);
    setDialogOpen(true);
  };

  const handleApplySuggestion = () => {
    if (suggestion) {
      setFormData(prev => ({
        ...prev,
        quantity_target: suggestion.suggested_quantity,
        revenue_target: suggestion.suggested_revenue,
      }));
      toast.success('Suggestion applied');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.quantity_target <= 0 && formData.revenue_target <= 0) {
      toast.error('Please set at least one target value');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        business_id: selectedBusinessId,
        store_id: formData.store_id || null,
      };
      
      if (selectedTarget) {
        await targetApi.update(selectedBusinessId, selectedTarget.id, {
          name: data.name,
          quantity_target: data.quantity_target,
          revenue_target: data.revenue_target,
          category_ids: data.category_ids,
        });
        toast.success('Target updated successfully');
      } else {
        await targetApi.create(selectedBusinessId, data);
        toast.success('Target created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Operation failed';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTarget) return;
    
    try {
      await targetApi.delete(selectedBusinessId, selectedTarget.id);
      toast.success('Target deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedTarget(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete target');
    }
  };

  const getStoreName = (storeId) => {
    if (!storeId) return 'All Branches';
    const store = stores.find(s => s.id === storeId);
    return store?.name || 'Unknown';
  };

  const getCategoryNames = (categoryIds) => {
    if (!categoryIds || categoryIds.length === 0) return 'All Categories';
    return categories
      .filter(c => categoryIds.includes(c.id))
      .map(c => c.name)
      .join(', ');
  };

  if (!selectedBusinessId) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <Target className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="font-serif text-2xl font-semibold mb-2">Select a Business</h2>
        <p className="text-muted-foreground">
          Choose a business from the dropdown above to view targets
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-coffee-espresso">
            Sales Targets
          </h1>
          <p className="text-muted-foreground mt-1">
            Set and track monthly sales goals
          </p>
        </div>
        {isManager && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-target-btn" className="rounded-xl">
            <Plus className="w-4 h-4 mr-2" />
            Add Target
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Label>Year:</Label>
              <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Month:</Label>
              <Select value={filterMonth ? String(filterMonth) : 'all'} onValueChange={(v) => setFilterMonth(v === 'all' ? null : Number(v))}>
                <SelectTrigger className="w-36" data-testid="filter-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Targets Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Targets ({targets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="responsive-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-right">Qty Target</TableHead>
                  <TableHead className="text-right">MTD Qty</TableHead>
                  <TableHead className="text-right">Qty Progress</TableHead>
                  {isManager && <TableHead className="text-right">Rev Target</TableHead>}
                  {isManager && <TableHead className="text-right">MTD Rev</TableHead>}
                  {isManager && <TableHead className="text-right">Rev Progress</TableHead>}
                  {isManager && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {targets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isManager ? 10 : 6} className="text-center py-8 text-muted-foreground">
                      No targets found for this period
                    </TableCell>
                  </TableRow>
                ) : (
                  targets.map((target) => (
                    <TableRow key={target.id}>
                      <TableCell className="font-medium">
                        {target.name || <span className="text-muted-foreground italic">Unnamed</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {MONTHS[target.month - 1]} {target.year}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="rounded-full">
                          {getStoreName(target.store_id)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(target.quantity_target, 2)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {formatNumber(target.mtd_quantity || 0, 2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                (target.quantity_progress || 0) >= 100 
                                  ? 'bg-green-500' 
                                  : (target.quantity_progress || 0) >= 75 
                                    ? 'bg-yellow-500' 
                                    : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(target.quantity_progress || 0, 100)}%` }}
                            />
                          </div>
                          <span className={`text-sm font-mono ${
                            (target.quantity_progress || 0) >= 100 
                              ? 'text-green-600' 
                              : (target.quantity_progress || 0) >= 75 
                                ? 'text-yellow-600' 
                                : 'text-red-600'
                          }`}>
                            {formatNumber(target.quantity_progress || 0, 1)}%
                          </span>
                        </div>
                      </TableCell>
                      {isManager && (
                        <>
                          <TableCell className="text-right font-mono">
                            R{formatNumber(target.revenue_target)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold">
                            R{formatNumber(target.mtd_revenue || 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${
                                    (target.revenue_progress || 0) >= 100 
                                      ? 'bg-green-500' 
                                      : (target.revenue_progress || 0) >= 75 
                                        ? 'bg-yellow-500' 
                                        : 'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(target.revenue_progress || 0, 100)}%` }}
                                />
                              </div>
                              <span className={`text-sm font-mono ${
                                (target.revenue_progress || 0) >= 100 
                                  ? 'text-green-600' 
                                  : (target.revenue_progress || 0) >= 75 
                                    ? 'text-yellow-600' 
                                    : 'text-red-600'
                              }`}>
                                {formatNumber(target.revenue_progress || 0, 1)}%
                              </span>
                            </div>
                          </TableCell>
                        </>
                      )}
                      {isManager && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(target)}
                              data-testid={`edit-target-${target.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedTarget(target);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-target-${target.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Info Card for Staff */}
      {!isManager && (
        <Card className="bg-muted/50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Monthly Targets</h3>
                <p className="text-sm text-muted-foreground">
                  These targets help track sales performance. The daily average is calculated based on 
                  working days (Monday to Saturday). Contact your manager if you have questions about the targets.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedTarget ? 'Edit Target' : 'Add New Target'}</DialogTitle>
            <DialogDescription>
              Set sales targets for a specific month and branch
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="target-name">Target Name</Label>
              <Input
                id="target-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Q1 Coffee Sales, Holiday Season Target"
                data-testid="target-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Year</Label>
                <Select 
                  value={String(formData.year)} 
                  onValueChange={(v) => setFormData({ ...formData, year: Number(v) })}
                  disabled={!!selectedTarget}
                >
                  <SelectTrigger data-testid="target-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Month</Label>
                <Select 
                  value={String(formData.month)} 
                  onValueChange={(v) => setFormData({ ...formData, month: Number(v) })}
                  disabled={!!selectedTarget}
                >
                  <SelectTrigger data-testid="target-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Branch</Label>
              <Select 
                value={formData.store_id || 'all'} 
                onValueChange={(v) => setFormData({ ...formData, store_id: v === 'all' ? '' : v })}
                disabled={!!selectedTarget}
              >
                <SelectTrigger data-testid="target-store">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Admin can select "All Branches", non-admin users cannot */}
                  {isAdmin && <SelectItem value="all">All Branches</SelectItem>}
                  {stores
                    .filter(store => isAdmin || userBranchIds.length === 0 || userBranchIds.includes(store.id))
                    .map((store) => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Suggestion Banner */}
            {suggestion && suggestion.suggested_quantity > 0 && (
              <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Suggested Targets</p>
                    <p className="text-sm text-muted-foreground">
                      Based on {suggestion.based_on.replace('_', ' ')}: {formatNumber(suggestion.suggested_quantity, 2)} qty, 
                      R{formatNumber(suggestion.suggested_revenue)} revenue
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      ({suggestion.working_days} working days)
                    </p>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={handleApplySuggestion}
                    >
                      Apply Suggestion
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity_target">Quantity Target</Label>
                <Input
                  id="quantity_target"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.quantity_target}
                  onChange={(e) => setFormData({ ...formData, quantity_target: Number(e.target.value) })}
                  data-testid="target-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="revenue_target">Revenue Target (R)</Label>
                <Input
                  id="revenue_target"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.revenue_target}
                  onChange={(e) => setFormData({ ...formData, revenue_target: Number(e.target.value) })}
                  data-testid="target-revenue"
                />
              </div>
            </div>

            <MultiSelectFilter
              label="Categories (contributing to target)"
              options={categories.map((c) => ({ id: c.id, name: c.name }))}
              selected={formData.category_ids}
              onChange={(ids) => setFormData({ ...formData, category_ids: ids })}
              testId="target-categories"
            />
            <p className="text-xs text-muted-foreground -mt-2">
              Leave empty to include all categories
            </p>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-target-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Target'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Target</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this target for {selectedTarget && MONTHS[selectedTarget.month - 1]} {selectedTarget?.year}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-target"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Targets;
